<template>
  <div class="DashboardDisplay">
    <div v-if="canSearchAssetHistory">
      <div class="d-flex flex-row">
        <h1 class="flex-grow-1" data-cy="Dashboard-title">{{ name }}</h1>
        <b-button
          data-cy="Dashboard-delete"
          variant="outline-primary"
          class="mt-2"
          @click="deleteDashboard"
          >Delete dashboard
        </b-button>
      </div>
      <div class="d-flex flex-row mt-4 mb-2">
        <div class="flex-grow-1">
          <DateTimePicker
            :dropdownRef="`startDate`"
            :dropdownText="`Start date`"
            :datePickerValue="datePickerStart"
            :timePickerValue="timePickerStart"
            class="mr-2"
            @update-date="updateStartDate"
            @update-time="updateStartTime"
          />
          <DateTimePicker
            :dropdownRef="`endDate`"
            :dropdownText="`End date`"
            :datePickerValue="datePickerEnd"
            :timePickerValue="timePickerEnd"
            class="mr-2"
            @update-date="updateEndDate"
            @update-time="updateEndTime"
          />
          <div class="d-inline-block">
            <b-form-select
              v-model="rangePicker"
              :options="graphTimeRangeOptions"
            ></b-form-select>
          </div>
        </div>
        <div>
          <b-button
            variant="outline-primary"
            class="mr-2"
            v-bind:disabled="disableApply"
            @click="applyDateTimeRange"
            >Apply
          </b-button>
          <b-button variant="outline-danger" @click="resetDateTimeRange"
            >Reset
          </b-button>
        </div>
      </div>
      <div>
        <b-card class="mb-5">
          <b-overlay :show="isLoading" spinner-variant="primary" opacity="1">
            <LineChart
              v-for="graphicType in assetGraphicData"
              :key="graphicType.type"
              class="mb-2"
              v-bind:assetGraphicData="graphicType.assetList"
              v-bind:graphicType="graphicType.type"
            />
          </b-overlay>
        </b-card>
      </div>
    </div>
    <PageNotAllowed v-else />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import LineChart from '../components/charts/LineChart';
import DateTimePicker from '../components/DateTimePicker';
import { measureFetcher } from '../measure_factory/MeasureFetcher';
import { measureFactory } from '../measure_factory/MeasureFactory';
import { MeasureType } from '../measure_factory/MeasureType';
import PageNotAllowed from '../components/PageNotAllowed';
import { canSearchAssetHistory } from '@/helpers/rights.js';

export default {
  name: 'DashboardDisplay',
  components: {
    LineChart,
    PageNotAllowed,
    DateTimePicker
  },
  data() {
    return {
      isLoading: true,
      isEndDateCloseable: false,
      name: '',
      assetList: [],
      assetType: [],
      assetGraphicData: [],
      datePickerStart: '',
      datePickerEnd: '',
      timePickerStart: '',
      timePickerEnd: '',
      isDatePicked: false,
      graphTimeRangeOptions: [
        { value: '1h', text: 'Hour' },
        { value: '1d', text: 'Day' },
        { value: '7d', text: 'Week' }
      ],
      rangePicker: '1h'
    };
  },

  computed: {
    ...mapGetters('tenants', ['selectedTenant']),
    ...mapGetters('auth', ['currentUserRights']),
    canSearchAssetHistory,
    startTimestamp() {
      return Date.parse(`${this.datePickerStart}T${this.timePickerStart}`);
    },
    endTimestamp() {
      return Date.parse(`${this.datePickerEnd}T${this.timePickerEnd}`);
    },
    disableApply() {
      return (
        this.datePickerStart == '' ||
        this.datePickerEnd == '' ||
        this.timePickerStart == '' ||
        this.timePickerEnd == ''
      );
    }
  },

  async mounted() {
    await this.fetchDashboardAsset();
    await this.fetchGraphicData();
  },

  methods: {
    ...mapActions('dashboard', ['DELETE_DASHBOARD']),

    updateStartDate(date) {
      this.datePickerStart = date;
    },

    updateStartTime(time) {
      this.timePickerStart = time;
    },

    updateEndDate(date) {
      this.datePickerEnd = date;
    },

    updateEndTime(time) {
      this.timePickerEnd = time;
    },

    applyDateTimeRange() {
      this.isDatePicked = true;
    },

    resetDateTimeRange() {
      this.isDatePicked = false;
      this.datePickerStart = '';
      this.datePickerEnd = '';
      this.timePickerStart = '';
      this.timePickerEnd = '';
    },

    handleDeleteConfirmation() {
      return this.$bvModal.msgBoxConfirm(
        'Are you sure you want to delete this dashboard?'
      );
    },

    editDashboard() {
      this.$router.push(`/dashboards/edit/${this.$route.params.dashboardId}`);
    },

    async deleteDashboard() {
      const isDeleteConfirmed = await this.handleDeleteConfirmation();

      if (!isDeleteConfirmed) {
        return;
      }

      this.isLoading = true;

      try {
        await this.DELETE_DASHBOARD({
          kuzzle: this.$kuzzle,
          tenantIndex: this.selectedTenant.index,
          dashboardId: this.$route.params.dashboardId
        });
        this.$router.push({ name: 'new-dashboard' });
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }
      this.isLoading = false;
    },

    async fetchDashboardAsset() {
      this.assetList = [];
      this.assetType = [];
      this.assetGraphicData = [];
      this.isLoading = true;

      try {
        const responseAssetList = await this.$kuzzle.document.get(
          this.selectedTenant.index,
          'dashboards',
          this.$route.params.dashboardId
        );
        this.name = responseAssetList._source.name;
        const assetList = responseAssetList._source.assetIds;
        const responseAsset = await this.$kuzzle.document.mGet(
          this.selectedTenant.index,
          'assets',
          assetList
        );

        this.assetList = responseAsset.successes.map(asset => ({
          assetId: asset._id,
          typeList: Object.keys(asset._source.measures)
          //we can add here a unitList who will match the type in typeList
        }));

        if (responseAsset.errors.length) {
          this.$log.error('Something went wrong while fetching the assets');
          this.$log.error(JSON.stringify(responseAsset.errors));
        }
        // TODO convert this to .map
        for (let i = 0; i < this.assetList.length; i++) {
          for (let j = 0; j < this.assetList[i].typeList.length; j++) {
            if (!this.assetType.includes(this.assetList[i].typeList[j])) {
              this.assetType.push(this.assetList[i].typeList[j]);
            }
          }
        }
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }
    },

    async fetchAssetHistory(assetId, index, type) {
      const measureHistory = [];
      const dateHistory = [];
      const field = measureFetcher(type);
      const measurePath = `asset.measures.${type}.updatedAt`;
      const query = {
        bool: {
          must: [
            {
              term: {
                assetId
              }
            }
          ]
        }
      };

      const rangeQuery = {
        nested: {
          path: 'asset',
          query: {
            bool: {
              must: [
                {
                  range: {
                    [measurePath]: {
                      gt: this.startTimestamp,
                      lt: this.endTimestamp
                    }
                  }
                }
              ]
            }
          }
        }
      };

      if (this.isDatePicked) {
        query.bool.must.push(rangeQuery);
      }

      const body = {
        query,
        aggs: {
          asset: {
            nested: {
              path: 'asset'
            },
            aggs: {
              measure_over_time: {
                date_histogram: {
                  field: measurePath,
                  fixed_interval: this.rangePicker
                },
                aggs: {
                  average: {
                    avg: {
                      field: field
                    }
                  }
                }
              }
            }
          }
        }
      };

      this.$log.debug(body);

      try {
        const response = await this.$kuzzle.document.search(
          this.selectedTenant.index,
          'asset-measures-history',
          body,
          { size: 1 }
        );
        for (const measure of response.aggregations.asset.measure_over_time
          .buckets) {
          measureHistory.push(measure.average.value);
          dateHistory.push(measure.key);
        }
        for (const asset of response.hits) {
          this.assetGraphicData[index].assetList.push(
            measureFactory(measureHistory, dateHistory, asset._source, type)
          );
        }
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }
    },

    async fetchGraphicData() {
      const promises = [];

      for (const assetType of this.assetType) {
        //we can add the unit as parameter but it have to be changed on the MeasureType class
        if (assetType !== 'movement' && assetType !== 'position') {
          this.assetGraphicData.push(new MeasureType(assetType));
        }
      }

      for (let i = 0; i < this.assetList.length; i++) {
        for (let j = 0; j < this.assetList[i].typeList.length; j++) {
          if (
            this.assetList[i].typeList[j] !== 'movement' &&
            this.assetList[i].typeList[j] !== 'position'
          ) {
            promises.push(
              this.fetchAssetHistory(
                this.assetList[i].assetId,
                this.assetType.indexOf(this.assetList[i].typeList[j]),
                this.assetList[i].typeList[j]
              )
            );
          }
        }
      }
      await Promise.all(promises);
      this.isLoading = false;
    },

    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Sorry, an error has occurred.',
        variant: 'danger',
        toaster: 'b-toaster-bottom-right',
        appendToast: true,
        dismissible: true,
        noAutoHide: true
      });
    }
  },
  watch: {
    '$route.params.dashboardId': {
      handler: async function() {
        await this.fetchDashboardAsset();
        await this.fetchGraphicData();
      },
      deep: true
    },
    async isDatePicked() {
      await this.fetchDashboardAsset();
      await this.fetchGraphicData();
    },
    async rangePicker() {
      await this.fetchDashboardAsset();
      await this.fetchGraphicData();
    }
  }
};
</script>
